import * as Yup from 'yup';
import { FormikContextType, FormikHelpers, useFormik } from 'formik';
import { Box, Collapse, FormLabel, Grid, Button, Alert } from '@mui/material';
import AutoCompleteFormField from '@acorn-portal/shared/src/components/inputs/AutoCompleteFormField';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { Modification } from '@acorn-portal/shared/src/services/api';
import { formatCurrency } from '@acorn-portal/shared/src/utils';
import FormTextField from '@acorn-portal/shared/src/components/inputs/FormTextField';
import RadioGroupButton from '@acorn-portal/shared/src/components/inputs/radio-button-group';
import HelperModal from '@acorn-portal/shared/src/components/modal/HelperModal';
import { FC, useState, useEffect } from 'react';
import { MODIFICATION_ERROR_MESSAGES } from '@acorn-portal/shared/src/utils/constants/ErrorMessages';
import MiniTable, { IColumn, TableType } from '../../components/table/mini-table';
import { useFormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';

interface IFormState {
	modificationCode: string;
	value: string;
	isForMedicalReasons: string;
}

interface IProps {
	onChange: (value: Modification[]) => void;
	hasGlobalError?: boolean;
	value: Modification[];
}

const LIMIT = 5;

const validationSchema = Yup.object().shape({
	modificationCode: Yup.string().required(MODIFICATION_ERROR_MESSAGES.modificationCode),
	value: Yup.number()
		.min(1, MODIFICATION_ERROR_MESSAGES.valueGreaterThanZero)
		.required(MODIFICATION_ERROR_MESSAGES.valueRequired),
	isForMedicalReasons: Yup.string().required(MODIFICATION_ERROR_MESSAGES.isForMedicalReasons),
});

const ModificationForm: FC<IProps> = ({ onChange, hasGlobalError, value }) => {
	const [selectedItemIndex, setSelectedItem] = useState<number>();
	const [open, toggleModal] = useState(false);
	const [modifications, setModifications] = useState<Modification[]>(value);
	const [showForm, toggleForm] = useState(modifications.length === 0);
	const lists = useAppSelector((state) => state.referenceData);

	// Check if modifications have reached the limit
	const isLimitReached = modifications.length >= LIMIT;

	const onHandleSubmit = (values: IFormState, formikHelpers: FormikHelpers<IFormState>) => {
		if (typeof selectedItemIndex !== 'undefined' && Number(selectedItemIndex) >= 0) {
			const newArray = [...modifications];
			newArray[selectedItemIndex] = {
				...newArray[selectedItemIndex],
				modificationCode: values.modificationCode,
				value: parseFloat(values.value),
				isForMedicalReasons: values.isForMedicalReasons === 'yes',
			};
			setModifications(newArray);
			setSelectedItem(undefined);
			toggleForm(false);
			formikHelpers.resetForm();
			return;
		}
		if (modifications.length >= LIMIT) {
			return;
		}
		const item: Modification = {
			modificationCode: values.modificationCode,
			value: parseFloat(values.value),
			isForMedicalReasons: values.isForMedicalReasons === 'yes',
			isCurrentInsurerNotified: false,
		};
		setModifications((prev) => [...prev, item]);
		toggleForm(false);
		formikHelpers.resetForm();
	};

	const formik = useFormik<IFormState>({
		initialValues: {
			modificationCode: '',
			value: '',
			isForMedicalReasons: '',
		},
		validationSchema,
		onSubmit: onHandleSubmit,
	});

	useFormikLogErrors(formik as FormikContextType<unknown>);

	const upDateForm = () => {
		onChange(modifications);
	};

	useEffect(() => {
		if (modifications.length > 0 || modifications !== value) {
			upDateForm();
		}
	}, [modifications, value]);

	const GetValue = (x: string) =>
		(lists.vehicleModificationsList.find((item) => item.value === x)?.label as string) || x;

	const COLUMNS = [
		{
			title: 'Modification',
			field: 'modificationCode',
			render: (rowData: { modificationCode: string }) => GetValue(rowData.modificationCode),
		},
		{
			title: 'Value',
			field: 'value',
			render: (rowData: { value: number }) => formatCurrency(rowData.value),
		},
	] as IColumn<Modification>[];

	const onEdit = async (item: Modification, index: number) => {
		await formik.setFieldValue('modificationCode', item.modificationCode);
		await formik.setFieldValue('value', item.value.toFixed(2));
		await formik.setFieldValue('isForMedicalReasons', item.isForMedicalReasons ? 'yes' : 'no');
		setSelectedItem(index);
		toggleForm(true);
	};

	const onRemove = (_item: Modification, index: number) => {
		setSelectedItem(index);
		toggleModal(true);
	};

	const onDelete = () => {
		if (typeof selectedItemIndex === 'undefined') {
			toggleModal(false);
			return;
		}
		const newArry = [...modifications];
		newArry.splice(selectedItemIndex, 1);
		if (newArry.length === 0) {
			formik.resetForm();
			toggleForm(true);
		}
		setModifications(newArry);
		toggleModal(false);
		setSelectedItem(undefined);
	};

	return (
		<Box>
			<Box
				sx={(theme) => ({
					pr: '20%',
					flexDirection: 'column',
					justifyContent: 'space-around',
					[theme.breakpoints.down('md')]: {
						pr: 'unset',
					},
				})}
			>
				{modifications.length > 0 && (
					<Box>
						<Grid container direction='row' spacing={1}>
							<Grid
								size={{ xs: 12, md: 4 }}
								sx={(theme) => ({
									textAlign: 'right',
									[theme.breakpoints.down('md')]: { textAlign: 'unset' },
								})}
							>
								<FormLabel>Modifications</FormLabel>
							</Grid>
							<Grid size={{ xs: 12, md: 8 }}>
								<MiniTable
									data={modifications}
									columns={COLUMNS}
									onRemove={onRemove}
									onEdit={onEdit}
									isEditing={showForm}
									tableType={TableType.MODIFICATIONS}
								/>
								<Collapse in={!showForm}>
									<Box>
										{/* Show an info alert when limit is reached */}
										{isLimitReached && (
											<Alert severity='info' sx={{ mt: 2, mb: 1 }}>
												You&apos;ve reached the maximum limit of {LIMIT} modifications.
											</Alert>
										)}
										<Box display='flex' justifyContent='flex-end'>
											<Button
												sx={(theme) => ({
													my: 2,
													[theme.breakpoints.down('md')]: {
														width: '100%',
													},
												})}
												variant='contained'
												color='primary'
												onClick={() => toggleForm(true)}
												// Disable button when limit is reached
												disabled={isLimitReached}
											>
												Add Another Modification
											</Button>
										</Box>
									</Box>
								</Collapse>
							</Grid>
						</Grid>
					</Box>
				)}
			</Box>
			<Collapse in={showForm}>
				{showForm && (
					<Box
						sx={(theme) => ({
							backgroundColor: theme.palette.grey[300],
							borderRadius: 2,
							my: 1,
							p: 2,
							'& .MuiOutlinedInput-root': {
								backgroundColor: theme.palette.common.white,
							},
						})}
					>
						<AutoCompleteFormField
							label='Modifications'
							name='modificationCode'
							id='modificationCode'
							value={formik.values.modificationCode}
							onChange={(x) => formik.setFieldValue('modificationCode', x)}
							options={lists.vehicleModificationsList}
							error={(formik.touched.modificationCode && Boolean(formik.errors.modificationCode)) || hasGlobalError}
							helperText={formik.touched.modificationCode && formik.errors.modificationCode}
						/>
						<FormTextField
							id='value'
							name='value'
							label='Value'
							error={(formik.touched.value && Boolean(formik.errors.value)) || hasGlobalError}
							helperText={formik.touched.value && formik.errors.value}
							onChange={formik.handleChange}
							value={formik.values.value}
							format='currency'
						/>
						<RadioGroupButton
							label='Is this for medical reasons?'
							name='isForMedicalReasons'
							id='isForMedicalReasons'
							value={formik.values.isForMedicalReasons}
							onChange={(x) => formik.setFieldValue('isForMedicalReasons', x)}
							error={
								(formik.touched.isForMedicalReasons && Boolean(formik.errors.isForMedicalReasons)) || hasGlobalError
							}
							helperText={formik.touched.isForMedicalReasons && formik.errors.isForMedicalReasons}
						/>
						<Box
							display='flex'
							justifyContent='flex-end'
							sx={(theme) => ({
								pr: '20%',
								[theme.breakpoints.down('md')]: {
									pr: 'unset',
								},
							})}
						>
							<Button
								sx={(theme) => ({
									my: 2,
									mr: 2,
									minWidth: '10%',
									[theme.breakpoints.down('md')]: {
										width: '50%',
									},
								})}
								variant='contained'
								color='secondary'
								disabled={modifications.length === 0}
								onClick={() => {
									toggleForm(false);
									formik.resetForm();
									setSelectedItem(undefined);
								}}
							>
								Close
							</Button>
							<Button
								sx={(theme) => ({
									my: 2,
									minWidth: '100px',
									[theme.breakpoints.down('md')]: {
										width: '50%',
									},
								})}
								variant='contained'
								color='primary'
								onClick={() => formik.handleSubmit()}
							>
								{typeof selectedItemIndex !== 'undefined' && Number(selectedItemIndex) >= 0 ? 'Edit' : 'Add'}
							</Button>
						</Box>
					</Box>
				)}
			</Collapse>
			<HelperModal
				open={open}
				onClose={() => {
					setSelectedItem(undefined);
					toggleModal(false);
				}}
				title='Remove Modification'
				description='Are you sure?'
				closeText='Cancel'
				submitText='Delete'
				onSubmit={() => onDelete()}
			/>
		</Box>
	);
};

export default ModificationForm;
